import type { CompetitionParticipant } from '../../../types/competition';
import type { CompetitionRound, RoundClimb } from '../../../types/competition-round';
import { byBlockName } from '../blockOrder';
import {
  getParticipantDisplayText,
  getParticipantName,
  getParticipantSurname,
  getParticipantTeam,
} from '../participantUtils';

/**
 * **A block's name is the block on the wall.** Every class of a round has its
 * own numbered problems — "Blocco 3" of the Junior and "Blocco 3" of the Senior
 * are two rows — but they are one sign on one boulder, and the judge standing at
 * it scores everybody who comes to it. So a judge picks a *name*, and the classes
 * that climb it are the tabs of that one block.
 */
export function blockKey(roundClimb: RoundClimb): string {
  return (roundClimb.display_name ?? '').trim().toLocaleLowerCase();
}

/** One block on the wall: its name, and the round's problems that carry it. */
export interface WallBlock {
  key: string;
  name: string;
  roundClimbs: RoundClimb[];
}

/** The round's problems grouped by name, in the order they are numbered. */
export function wallBlocks(roundClimbs: RoundClimb[]): WallBlock[] {
  const groups = new Map<string, WallBlock>();
  for (const roundClimb of [...roundClimbs].sort(byBlockName)) {
    const key = blockKey(roundClimb);
    const group = groups.get(key);
    if (group) group.roundClimbs.push(roundClimb);
    else groups.set(key, { key, name: roundClimb.display_name ?? '', roundClimbs: [roundClimb] });
  }
  return [...groups.values()];
}

/**
 * The problem of this block a class climbs: its own numbered one, or the one the
 * whole round shares.
 */
export function problemForClass(block: WallBlock, categoryId: string | null): RoundClimb | null {
  return (
    block.roundClimbs.find((rc) => rc.competition_category_id === categoryId) ??
    block.roundClimbs.find((rc) => rc.competition_category_id === null) ??
    null
  );
}

/** Whether this viewer may score on any of the block's problems. */
export function blockOpenToMe(block: WallBlock): boolean {
  return block.roundClimbs.some((rc) => rc.can_score !== false);
}

/**
 * Where a judge lands when they pick a block: the class they were last scoring,
 * if it climbs this block and they may score it there, or else the first class
 * they may, in the order of the tabs (`classOrder`).
 */
export function landingProblem(
  block: WallBlock,
  preferredCategoryId: string | null,
  classOrder: string[] = []
): RoundClimb | null {
  const open = (rc: RoundClimb | null): rc is RoundClimb => !!rc && rc.can_score !== false;
  const preferred = preferredCategoryId ? problemForClass(block, preferredCategoryId) : null;
  if (open(preferred)) return preferred;
  for (const categoryId of classOrder) {
    const problem = problemForClass(block, categoryId);
    if (open(problem)) return problem;
  }
  return block.roundClimbs.find(open) ?? block.roundClimbs[0] ?? null;
}

/** The page a problem is scored on, for the round's discipline. */
export function judgePagePath(slug: string, round: Pick<CompetitionRound, 'id' | 'round_type'>, roundClimb: RoundClimb, categoryId?: string | null): string {
  const page = round.round_type === 'Lead' ? 'judge-lead' : 'judge-boulder';
  const query = categoryId ? `?category=${encodeURIComponent(categoryId)}` : '';
  return `/competitions/${slug}/rounds/${round.id}/climbs/${roundClimb.id}/${page}${query}`;
}

/** What a block's page hands the table of one class. */
export interface JudgeTableProps {
  competitionSlug: string;
  round: CompetitionRound;
  roundClimb: RoundClimb;
  /** This class's athletes on this round, the judge themself left out. */
  participants: CompetitionParticipant[];
  filters: JudgeFilters;
  onFiltersChange: (filters: JudgeFilters) => void;
  /** The round still takes marks: running, in its corrections or under review. */
  scoringOpen: boolean;
}

// --- The class last scored, per round ---------------------------------------

const CLASS_STORAGE_PREFIX = 'judge-class:';

export function loadJudgeClass(slug: string | undefined, roundId: string | undefined): string | null {
  if (!slug || !roundId) return null;
  try {
    return sessionStorage.getItem(`${CLASS_STORAGE_PREFIX}${slug}:${roundId}`) || null;
  } catch {
    return null;
  }
}

export function storeJudgeClass(slug: string | undefined, roundId: string | undefined, categoryId: string) {
  if (!slug || !roundId) return;
  try {
    sessionStorage.setItem(`${CLASS_STORAGE_PREFIX}${slug}:${roundId}`, categoryId);
  } catch {
    // Private mode or blocked storage: the next block opens on its first class.
  }
}

// --- Search and sort, per class, for the whole round ------------------------

export type JudgeSortColumn = 'name' | 'surname' | 'team';
export type JudgeSortOrder = 'asc' | 'desc';

export interface JudgeFilters {
  query: string;
  column: JudgeSortColumn;
  order: JudgeSortOrder;
}

export const DEFAULT_JUDGE_FILTERS: JudgeFilters = { query: '', column: 'surname', order: 'asc' };

/** The key a round with no classes keeps its one table's filters under. */
export const NO_CLASS = '_';

/**
 * Kept per class for the whole round, in the browser's session: moving from
 * Blocco 3 to Blocco 4 keeps the Junior's search and the Senior's sort, each its
 * own, because the judge goes on looking for the same people.
 */
const FILTERS_STORAGE_PREFIX = 'judge-filters:';

export function loadJudgeFilters(slug: string | undefined, roundId: string | undefined): Record<string, JudgeFilters> {
  if (!slug || !roundId) return {};
  try {
    const stored = sessionStorage.getItem(`${FILTERS_STORAGE_PREFIX}${slug}:${roundId}`);
    if (!stored) return {};
    const parsed = JSON.parse(stored) as Record<string, Partial<JudgeFilters>>;
    return Object.fromEntries(
      Object.entries(parsed).map(([key, value]) => [key, { ...DEFAULT_JUDGE_FILTERS, ...value }])
    );
  } catch {
    return {};
  }
}

export function storeJudgeFilters(
  slug: string | undefined,
  roundId: string | undefined,
  filters: Record<string, JudgeFilters>
) {
  if (!slug || !roundId) return;
  try {
    sessionStorage.setItem(`${FILTERS_STORAGE_PREFIX}${slug}:${roundId}`, JSON.stringify(filters));
  } catch {
    // Private mode or quota: the filters still work, they are just not kept.
  }
}

const SORT_VALUES: Record<JudgeSortColumn, (participant: CompetitionParticipant) => string> = {
  name: getParticipantName,
  surname: getParticipantSurname,
  team: getParticipantTeam,
};

/**
 * The search box and the sort, with **finished business at the bottom** under
 * every sort: what is left in front of the judge is the people still to climb.
 * What "finished" means is the discipline's (a top, a card).
 */
export function arrangeParticipants(
  participants: CompetitionParticipant[],
  filters: JudgeFilters,
  done: (participant: CompetitionParticipant) => boolean
): CompetitionParticipant[] {
  const query = filters.query.trim().toLowerCase();
  const found = query
    ? participants.filter(
        (p) => getParticipantDisplayText(p).toLowerCase().includes(query) || p.email.toLowerCase().includes(query)
      )
    : participants;

  return [...found].sort((a, b) => {
    const doneDiff = Number(done(a)) - Number(done(b));
    if (doneDiff !== 0) return doneDiff;

    const comparison = SORT_VALUES[filters.column](a).localeCompare(SORT_VALUES[filters.column](b), undefined, {
      sensitivity: 'base',
    });
    return filters.order === 'asc' ? comparison : -comparison;
  });
}

/** A header pressed: the same column flips, another one starts ascending. */
export function sortBy(filters: JudgeFilters, column: JudgeSortColumn): JudgeFilters {
  if (column === filters.column) return { ...filters, order: filters.order === 'asc' ? 'desc' : 'asc' };
  return { ...filters, column, order: 'asc' };
}
